import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Box, Button, Modal, Stack } from '@mui/material';
import { FormContainer } from '../../../styles/styleUtils';
import CustomerData from '../components/CustomerData';
import ActionButtons from '../components/ActionButtons';
import ImportaDocumento from '../components/ImportaDados/ImportaDocumento';
import ModalLoading from '../../ModalLoading';
import PaymentDetails from './comp/PaymentDetails';
import CreditoDetalhes from './comp/CreditoDetalhes';
import NumeroIdentificador from './comp/NumeroIdentificador';
import BancoEmissor from './comp/BancoEmissor';
import DocumentoDetalhes from './comp/DocumentoDetalhes';
import Parcelas from './comp/Parcelas';
import calculaEncargos from '../funcs/calculaEncargos';
import ComprovanteBaixa from '../components/ComprovanteBaixa';
import TipoReceita from './comp/TipoReceita';
import RecorrenciaReceita from './comp/RecorrenciaReceita';
import { estornarReceita } from '../../api';

const hojeNoFusoBrasileiro = () => {
  const now = new Date();
  const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // zera hora local
  return localDate;
};


const inferirTipoReceita = (dados = {}) => {
  if (['avulsa', 'parcelada', 'recorrente'].includes(dados.tipoReceita)) {
    return dados.tipoReceita;
  }
  if (dados.periodicidadeRecorrencia || dados.quantidadeRecorrencias) return 'recorrente';
  if (Number(dados.parcelas || dados.quantidadeParcelas || 1) > 1) return 'parcelada';
  return 'avulsa';
};

const montarEstadoInicial = (dados) => {
  if (dados) {
    const tipoReceita = inferirTipoReceita(dados);
    return {
      ...dados,
      tipoReceita,
      quantidadeParcelas: Number(dados.quantidadeParcelas || dados.parcelas || 1) || 1,
      quantidadeRecorrencias:
        Number(dados.quantidadeRecorrencias || dados.parcelas || 2) || 2,
      periodicidade:
        dados.periodicidade || dados.periodicidadeRecorrencia || '',
    };
  }

  return {
    data: hojeNoFusoBrasileiro(),
    tipoCliente: 'F',
    nomeCliente: '',
    cpf: '',
    nascimento: '',
    telefone: '',
    sexo: '',
    dataRecebimento: '',
    vencimento: hojeNoFusoBrasileiro(),
    tipoRecebimento: 'T',
    valor: '',
    valorBaixa: '',
    formaPagamento: '',
    tipoReceita: 'avulsa',
    quantidadeParcelas: 1,
    quantidadeRecorrencias: 2,
    periodicidade: '',
    valorParcelas: '',
    unidade: '',
    desconto: 0,
    acrescimo: 0,
    encargos: 0,
    antecipacaoAutomatica: '',
  };
};

const NewReceitaForm = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState(() => montarEstadoInicial(props.formData));
  const [openModal, setOpenModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(true);
  const registroJaBaixado = Boolean(props.formData?._id && props.formData?.dataRecebimento);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'tipoReceita') {
      setFormData((prev) => {
        // Em edição de um registro já criado, não recria/exclui o grupo.
        if (!props.onInclusao) {
          return { ...prev, tipoReceita: value };
        }

        if (value === 'avulsa') {
          return {
            ...prev,
            tipoReceita: 'avulsa',
            quantidadeParcelas: 1,
            quantidadeRecorrencias: 2,
            periodicidade: '',
          };
        }

        if (value === 'parcelada') {
          return {
            ...prev,
            tipoReceita: 'parcelada',
            quantidadeParcelas:
              Number(prev.quantidadeParcelas || 0) > 1
                ? Number(prev.quantidadeParcelas)
                : 2,
            periodicidade: prev.periodicidade || 'mensal',
            quantidadeRecorrencias: 2,
          };
        }

        return {
          ...prev,
          tipoReceita: 'recorrente',
          quantidadeParcelas: 1,
          quantidadeRecorrencias:
            Number(prev.quantidadeRecorrencias || 0) > 1
              ? Number(prev.quantidadeRecorrencias)
              : 2,
          periodicidade: prev.periodicidade || 'mensal',
          // Recorrência futura nasce pendente.
          dataRecebimento: '',
          valorBaixa: '',
          idContaRecebimento: null,
          contaRecebimento: null,
        };
      });
      return;
    }
    if (type === 'date' && event.target.value) {
      const [year, month, day] = value.split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // cria como local (sem UTC)
      setFormData({
        ...formData,
        [name]: localDate,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setModalLoading(true);
    setFormData({
      ...formData,
      [idName]: value?._id,
      [name]: value,
    });
    setModalLoading(false);
  };

  const handleCustomerTypeChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleConfirm = () => {
    const dados = {
      ...formData,
      tipoReceita: formData.tipoReceita || inferirTipoReceita(formData),
      quantidadeParcelas:
        formData.tipoReceita === 'recorrente'
          ? Number(formData.quantidadeRecorrencias || 2)
          : Number(formData.quantidadeParcelas || 1),
      quantidadeRecorrencias:
        formData.tipoReceita === 'recorrente'
          ? Number(formData.quantidadeRecorrencias || 2)
          : undefined,
      periodicidadeRecorrencia:
        formData.tipoReceita === 'recorrente'
          ? formData.periodicidade
          : undefined,
    };
    props.onConfirm(dados);
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja cancelar?')) {
      props.handleClose();
    }
  };

  const openImporta = () => {
    setOpenModal(true);
  };

  const closeImporta = () => {
    setOpenModal(false);
  };

  const onImportaDados = async (e) => {
    setModalLoading(true);
    const proposta = e;
    setFormData({
      ...formData,
      cliente: proposta.cliente,
      idCliente: proposta.cliente._id,
      // useEffect para outros valores de cliente
      unidade: proposta.unidade,
    });
    setModalLoading(false);
  };

  // 🧾 Atualiza valorBaixa e quantidadeParcelas quando valor ou tipoRecebimento mudam
  useEffect(() => {
    if ((!props.onInclusao || formData.dataRecebimento) && formData.valor && formData.tipoRecebimento === 'T') {
      setFormData((prev) => ({
        ...prev,
        valorBaixa: prev.valor,
        quantidadeParcelas:
          prev.tipoReceita === 'recorrente'
            ? Number(prev.quantidadeRecorrencias || 2)
            : 1,
      }));
    }
  }, [formData.valor, formData.tipoRecebimento, formData.dataRecebimento]);

  const subTotal = useMemo(() => {
    return (formData.valorBaixa ?? 0) - (formData.desconto ?? 0) + (formData.acrescimo ?? 0);
  }, [formData.valorBaixa, formData.desconto, formData.acrescimo]);

  // Calcular encargos
  useEffect(() => {
    if (subTotal <= 0) return;
    if (!formData.formaPagamento) return;
    const newEncargo = calculaEncargos(subTotal, formData, formData);
    if (formData.encargos !== newEncargo) {
      setFormData((prev) => ({
        ...prev,
        encargos: newEncargo,
      }));
    }
  }, [formData.formaPagamento, formData.operadora, formData.antecipacaoAutomatica, formData.bancoEmissor, subTotal]);

  // Atualiza formData.valorLiquido sempre que subTotal ou encargos mudarem
  useEffect(() => {
    if (subTotal > 0) {
      const novoValorLiquido = subTotal - (formData.encargos ?? 0);
      // Só atualiza se o valor for diferente para evitar loops desnecessários
      if (formData.valorLiquido !== novoValorLiquido) {
        setFormData((prev) => ({
          ...prev,
          valorLiquido: novoValorLiquido,
        }));
      }
    }
  }, [subTotal, formData.encargos]);

  useEffect(() => {
    if (!formData.dataRecebimento) {
      const newReceita = { ...formData };
      newReceita.tipoRecebimento = null;
      newReceita.valorBaixa = null;
      newReceita.desconto = null;
      newReceita.acrescimo = null;
      newReceita.encargos = null;
      newReceita.valorLiquido = null;
      newReceita.contaRecebimento = null;
      newReceita.idContaRecebimento = null;
      newReceita.operadora = null;
      newReceita.idOperadora = null;
      newReceita.bandeira = null;
      newReceita.idBandeira = null;
      newReceita.bancoEmissor = null;
      newReceita.idBancoEmissor = null;
      newReceita.antecipacaoAutomatica = null;
      newReceita.NumeroIdentificador = null;
      setFormData(newReceita);
    }
  }, [formData.dataRecebimento]);

  // Validation logic for disableConfirm
  useEffect(() => {
    const {
      idCliente,
      valor,
      vencimento,
      unidade,
      quantidadeParcelas,
      quantidadeRecorrencias,
      tipoReceita,
      periodicidade,
      idFormaPagamento,
      dataRecebimento,
      idContaRecebimento,
      formaPagamento,
      idOperadora,
      idBandeira,
      idBancoEmissor,
      antecipacaoAutomatica,
      parcela,
    } = formData;

    // Check required fields
    const areRequiredFieldsFilled = idFormaPagamento && idCliente && valor && vencimento && unidade && (quantidadeParcelas || parcela);

    let specificValidationsMet = true;

    // Receita parcelada e recorrente exigem quantidade e periodicidade.
    if (
      props.onInclusao &&
      tipoReceita === 'parcelada' &&
      (Number(quantidadeParcelas) < 2 || !periodicidade)
    ) {
      specificValidationsMet = false;
    }

    if (
      props.onInclusao &&
      tipoReceita === 'recorrente' &&
      (Number(quantidadeRecorrencias) < 2 || !periodicidade)
    ) {
      specificValidationsMet = false;
    }

    if (tipoReceita === 'recorrente' && dataRecebimento) {
      specificValidationsMet = false;
    }

    // Condition: se tiver dataRecebimento precisa ter idContaRecebimento
    if (dataRecebimento && !idContaRecebimento) {
      specificValidationsMet = false;
    }

    // Condition: se tiver dataRecebimento precisa ter idFormaPagamento
    if (dataRecebimento && !idFormaPagamento) {
      specificValidationsMet = false;
    }

    // Condition: verificar formData.formaPagamento.tipo se for = 'cartao' precisa ter idOperadora e idBandeira
    if (dataRecebimento && formaPagamento && formaPagamento.tipo === 'cartao') {
      if (!idOperadora || !idBandeira) {
        specificValidationsMet = false;
      }
    }

    // Condition: se formData.formaPagamento.tipo se for = 'boleto' precisa ter idBancoEmissor e antecipacaoAutomatica
    if (dataRecebimento && formaPagamento && formaPagamento.tipo === 'boleto') {
      if (
        !idBancoEmissor ||
        !antecipacaoAutomatica ||
        antecipacaoAutomatica === undefined ||
        antecipacaoAutomatica === ''
      ) {
        // Check for undefined as false is a valid boolean
        specificValidationsMet = false;
      }
    }

    // Condition: se formData.formaPagamento.tipo se for = 'boleto' precisa ter idBancoEmissor e antecipacaoAutomatica
    if (
      dataRecebimento &&
      formaPagamento &&
      formaPagamento.tipo === 'cartao' &&
      formaPagamento.nome.includes('Parcelado')
    ) {
      if (!antecipacaoAutomatica || antecipacaoAutomatica === undefined || antecipacaoAutomatica === '') {
        // Check for undefined as false is a valid boolean
        specificValidationsMet = false;
      }
    }

    setDisableConfirm(!(areRequiredFieldsFilled && specificValidationsMet));
  }, [formData]); // Re-run validation when formData changes

  useEffect(() => {
    if (!formData.tipoRecebimento && formData.dataRecebimento) {
      setFormData((prev) => ({
        ...prev,
        tipoRecebimento: 'T',
      }));
    }
  }, [formData]);

  const handleComprovanteUpload = (upload) => {
    setFormData((prev) => ({
      ...prev,
      comprovanteBaixaUpload: upload,
    }));
  };

  const handleEstorno = async () => {
    const motivo = window.prompt('Informe o motivo do estorno do recebimento:');
    if (!motivo || motivo.trim().length < 3) {
      window.alert('Informe um motivo válido para o estorno.');
      return;
    }

    setModalLoading(true);
    const result = await estornarReceita(formData._id, motivo.trim());
    setModalLoading(false);

    if (result?.success === false || result?.ok === false) {
      window.alert(result?.message || 'Não foi possível estornar o recebimento.');
      return;
    }

    window.alert(result?.message || 'Recebimento estornado com sucesso.');
    props.onEstornoConcluido?.();
    props.handleClose();
  };

  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FormContainer>
        {registroJaBaixado && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta receita já foi recebida e está bloqueada para edição. Para corrigir o lançamento, estorne a baixa primeiro.
          </Alert>
        )}

        <Box sx={registroJaBaixado ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
        <TipoReceita
          value={formData.tipoReceita || inferirTipoReceita(formData)}
          onChange={handleInputChange}
          disabled={Boolean(formData.idVenda) || !props.onInclusao}
        />

        <CustomerData
          formData={formData}
          onCustomerTypeChange={handleCustomerTypeChange}
          onOpen={openImporta}
          disabled={formData.visualiza}
          onInputChange={handleInputChange}
          onAutoCompleteChange={handleAutoCompleteChange}
        />

        <DocumentoDetalhes onInputChange={handleInputChange} formData={formData} />

        <PaymentDetails
          onCustomerTypeChange={handleCustomerTypeChange}
          onAutoCompleteChange={handleAutoCompleteChange}
          onInputChange={handleInputChange}
          formData={formData}
          tipoRecebimento={formData.tipoRecebimento}
          onInclusao={props.onInclusao}
        />

        {props.onInclusao &&
          !formData.dataRecebimento &&
          formData.tipoReceita === 'parcelada' && (
            <Parcelas onInputChange={handleInputChange} formData={formData} />
          )}

        {props.onInclusao && formData.tipoReceita === 'recorrente' && (
          <RecorrenciaReceita
            formData={formData}
            onInputChange={handleInputChange}
            disabled={registroJaBaixado}
          />
        )}

        {formData.dataRecebimento && formData.formaPagamento && formData.formaPagamento.nome.includes('Crédito') && (
          <CreditoDetalhes
            onAutoCompleteChange={handleAutoCompleteChange}
            onInputChange={handleInputChange}
            formData={formData}
          />
        )}

        {formData.dataRecebimento &&
          formData.formaPagamento &&
          ['Financiamento', 'Cheque', 'Boleto'].some((keyword) => formData.formaPagamento.nome.includes(keyword)) && (
            <NumeroIdentificador onInputChange={handleInputChange} formData={formData} />
          )}
        {formData.dataRecebimento && formData.formaPagamento && formData.formaPagamento.nome.includes('Boleto') && (
          <BancoEmissor
            onAutoCompleteChange={handleAutoCompleteChange}
            formData={formData}
            onInputChange={handleInputChange}
          />
        )}

        </Box>

        {(formData.dataRecebimento || formData.comprovanteBaixa || formData.comprovantesHistoricos?.length) && (
          <ComprovanteBaixa
            formData={formData}
            onUploadChange={handleComprovanteUpload}
            podeAnexar={!registroJaBaixado && Boolean(formData.dataRecebimento)}
            titulo="Comprovante do recebimento"
          />
        )}

        {registroJaBaixado ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={props.handleClose}>Fechar</Button>
            <Button color="warning" variant="contained" onClick={handleEstorno}>
              Estornar baixa
            </Button>
          </Stack>
        ) : (
          <ActionButtons onConfirm={handleConfirm} onCancel={handleCancel} disableConfirm={disableConfirm} />
        )}

        {openModal && (
          <ImportaDocumento open={openModal} handleClose={closeImporta} onSelect={onImportaDados} type={'vendas'} />
        )}
        {modalLoading && <ModalLoading open={modalLoading} />}
      </FormContainer>
    </Modal>
  );
});

/*
AddProposta.propTypes = {
  produto: PropTypes.object,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
}
*/

export default NewReceitaForm;
